'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import Link from 'next/link';
import type { Route } from 'next';
import { usePathname } from 'next/navigation';
import {
  BillingSummarySkeleton,
  BoardSkeleton,
  CardListSkeleton,
  DashboardSkeleton,
  DetailPageSkeleton,
  FormSkeleton,
  GanttSkeleton,
  PageContentSkeleton,
} from '@/components/skeletons';
import { Skeleton } from '@/components/ui/Skeleton';

interface NavigationPendingContextValue {
  pendingHref: string | null;
  isTransitioning: boolean;
  beginNavigation: (href: string) => void;
  markNavigationReady: () => void;
}

const NavigationPendingContext = createContext<NavigationPendingContextValue | null>(null);

const TRANSITION_SAFETY_MS = 8_000;

/** True once the URL reflects the destination the user clicked. */
export function isNavigationComplete(pendingHref: string, pathname: string): boolean {
  if (pendingHref === '/projects') {
    return pathname === '/projects';
  }
  return pathname === pendingHref || pathname.startsWith(`${pendingHref}/`);
}

/**
 * Tab switches inside a shared layout (settings, project settings) — keep chrome
 * visible and let the nested PendingMainContent show the route skeleton.
 */
export function isNestedLayoutTransition(pathname: string, pendingHref: string | null): boolean {
  if (!pendingHref) return false;

  if (pendingHref.startsWith('/settings') && pathname.startsWith('/settings')) {
    return true;
  }

  const projectSettings = /^\/projects\/[^/]+\/settings/;
  if (projectSettings.test(pendingHref) && projectSettings.test(pathname)) {
    const pendingProject = pendingHref.match(/^\/projects\/([^/]+)/)?.[1];
    const currentProject = pathname.match(/^\/projects\/([^/]+)/)?.[1];
    return pendingProject === currentProject;
  }

  return false;
}

/** Pathname for chrome (breadcrumbs, sidebar highlight) — optimistic while navigating. */
export function useEffectivePathname(): string {
  const pathname = usePathname();
  const { pendingHref, isTransitioning } = useNavigationPending();

  if (isTransitioning && pendingHref) {
    return pendingHref;
  }
  return pathname;
}

/** Shape-matched skeleton for optimistic main-content loading during route transitions. */
export function routeSkeletonForHref(href: string): ReactNode {
  if (href === '/dashboard' || href.startsWith('/dashboard/')) {
    return <DashboardSkeleton />;
  }
  if (href === '/projects') {
    return (
      <div className="max-w-5xl space-y-7">
        <div className="space-y-2">
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
        <CardListSkeleton />
      </div>
    );
  }
  if (href.includes('/board')) {
    return <BoardSkeleton />;
  }
  if (href.includes('/gantt')) {
    return <GanttSkeleton />;
  }
  if (href.startsWith('/settings')) {
    return <FormSkeleton fields={5} />;
  }
  if (href.startsWith('/marketplace')) {
    return (
      <div className="max-w-5xl space-y-7">
        <div className="space-y-2">
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <CardListSkeleton rows={4} />
      </div>
    );
  }
  if (href.match(/\/projects\/[^/]+\/settings/)) {
    return <FormSkeleton fields={4} />;
  }
  if (href.match(/\/marketplace\/(jobs|engagements|vendors)\/[^/]+/)) {
    return <DetailPageSkeleton />;
  }
  return <PageContentSkeleton />;
}

export function NavigationPendingProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const [pendingHref, setPendingHref] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTransition = useCallback(() => {
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
    setPendingHref(null);
    setIsTransitioning(false);
  }, []);

  const beginNavigation = useCallback(
    (href: string) => {
      if (isNavigationComplete(href, pathname)) {
        return;
      }
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
      }
      setPendingHref(href);
      setIsTransitioning(true);
      safetyTimerRef.current = setTimeout(clearTransition, TRANSITION_SAFETY_MS);
    },
    [pathname, clearTransition],
  );

  const markNavigationReady = useCallback(() => {
    clearTransition();
  }, [clearTransition]);

  useEffect(() => () => {
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
    }
  }, []);

  const value = useMemo(
    () => ({
      pendingHref,
      isTransitioning,
      beginNavigation,
      markNavigationReady,
    }),
    [pendingHref, isTransitioning, beginNavigation, markNavigationReady],
  );

  return (
    <NavigationPendingContext.Provider value={value}>
      {children}
    </NavigationPendingContext.Provider>
  );
}

export function useNavigationPending(): NavigationPendingContextValue {
  const ctx = useContext(NavigationPendingContext);
  if (!ctx) {
    throw new Error('useNavigationPending must be used within NavigationPendingProvider');
  }
  return ctx;
}

type NavigationAwareLinkProps = React.ComponentProps<typeof Link>;

/** Sets pending navigation on click so the main content area can show a skeleton immediately. */
export function NavigationAwareLink({
  href,
  onClick,
  children,
  ...props
}: NavigationAwareLinkProps) {
  const { beginNavigation } = useNavigationPending();
  const target = typeof href === 'string' ? href : (href.pathname ?? '');

  return (
    <Link
      href={href as Route}
      onClick={(event) => {
        if (!event.defaultPrevented && target) {
          beginNavigation(target);
        }
        onClick?.(event);
      }}
      {...props}
    >
      {children}
    </Link>
  );
}

/**
 * Shows a route skeleton while navigating. The destination page mounts invisibly
 * underneath so it can fetch data and call markNavigationReady() when ready.
 *
 * Root (app layout): pass through to nested layouts during tab switches so chrome stays visible.
 * Nested (settings / project-settings layout): always swap content for the route skeleton.
 */
export function PendingMainContent({
  children,
  nested = false,
}: {
  children: ReactNode;
  nested?: boolean;
}) {
  const pathname = usePathname();
  const { pendingHref, isTransitioning } = useNavigationPending();
  const deferToNestedLayout =
    !nested &&
    isTransitioning &&
    pendingHref !== null &&
    isNestedLayoutTransition(pathname, pendingHref);

  if (deferToNestedLayout) {
    return <>{children}</>;
  }

  return (
    <>
      <div
        className={isTransitioning ? 'hidden' : undefined}
        aria-hidden={isTransitioning}
      >
        {children}
      </div>
      {isTransitioning && pendingHref && routeSkeletonForHref(pendingHref)}
    </>
  );
}

/** Call from destination pages when their initial data is ready (clears the route skeleton). */
export function useMarkNavigationReady(ready: boolean): void {
  const { markNavigationReady, isTransitioning, pendingHref } = useNavigationPending();
  const pathname = usePathname();

  useEffect(() => {
    if (!isTransitioning || !ready) return;
    if (pendingHref && !isNavigationComplete(pendingHref, pathname)) {
      return;
    }
    markNavigationReady();
  }, [isTransitioning, ready, pendingHref, pathname, markNavigationReady]);
}
